import { Component, Input, OnInit } from '@angular/core';
import { ClrDatagridModule, ClrDatagridSortOrder, ClrModalModule } from "@clr/angular";
import { Components, Files } from "../../../_shared/interfaces";
import { DatePipe, NgIf } from "@angular/common";
import { NgxExtendedPdfViewerModule } from "ngx-extended-pdf-viewer";
import { UploadComponent } from "../../../core/upload/upload.component";
import { saveAs } from 'file-saver';
import { AlertService, AuthService, ComponentsStoreService, FileStoreService, IAlertType } from "../../../_shared/services";
import JSZip from 'jszip';

import { environment as env } from "../../../../environments/environment";
import { filesBaseUrl } from "../../../_shared/utils/rest-url";
import { TranslocoDirective } from "@jsverse/transloco";
import { ClarityIcons, trashIcon } from "@cds/core/icon";

@Component({
  selector: 'app-component-files',
  standalone: true,
  imports: [
    NgIf,
    ClrDatagridModule,
    DatePipe,
    ClrModalModule,
    NgxExtendedPdfViewerModule,
    UploadComponent,
    TranslocoDirective
  ],
  templateUrl: './component-files.component.html',
  styleUrl: './component-files.component.sass'
})
export class ComponentFilesComponent implements OnInit {
  public loading: boolean = true;
  public isDownloading: boolean = false;
  public downloadProgress: number = 0;
  components: Components[] = [];
  component: Components | null = null;
  componentsFiles: Files[] = [];
  descSort = ClrDatagridSortOrder.DESC;
  devMode: boolean;
  modalContent: string = '';
  modalUrl: string = '';
  modalSize: string = 'lg';
  public showPDF = false;
  public showFileUpload = false;
  endpoint = filesBaseUrl();

  @Input() componentID: string | any;

  constructor(
    private componentsService: ComponentsStoreService,
    private filesService: FileStoreService,
    private alertDispatcher: AlertService,
    private authService: AuthService
  ) {
    this.devMode = env.stages.dev;
    ClarityIcons.addIcons(trashIcon)
  }

  ngOnInit() {
    this.loading = true;
    this.getComponent(this.componentID);
    this.getFiles([0], this.componentID);
  }

  getComponent(coid: string) {
    this.componentsService.getSingleComponent(coid).subscribe({
      next: data => {
        if (!data || !data.object || data.object.length === 0) {
          this.alertDispatcher.show({
          message: ' Keine Komponente gefunden! ',
          clrAlertType: IAlertType.WARNING
          }, 2000);
          return;
        }
        this.components = data.object;
        this.component = this.components[0] || null;
      },
      complete: () => {
        if (this.component && !this.component.active) {
          this.alertDispatcher.show({
            message: ' \'Die Komponente \' + this.components.name + \' (Zeichnungsnummer: \' + this.components.technical_drawing + \') wurde auf PASSIV gesetzt! ',
            clrAlertType: IAlertType.WARNING
          }, 2000);
        }
        if (this.devMode) console.log('Get single component!');
      }
    })
  }

  // Document types that belong to warehouse operations — excluded from the
  // component documents view because they are uploaded in order/warehouse context
  // and would otherwise clutter the component's own document list.
  private readonly WAREHOUSE_DOCTYPES = new Set([75, 76]);

  getFiles(doctype: number[], componentID: string) {
    this.componentsService.getComponentsFile(doctype, '0', componentID, 0, 0).subscribe({
      next: data => {
        const all = data?.object ?? [];
        this.componentsFiles = all.filter(f => !this.WAREHOUSE_DOCTYPES.has(f.document_type));
        if (this.componentsFiles.length === 0) {
          this.alertDispatcher.show({
            message: ' Keine Dateien gefunden! ',
            clrAlertType: IAlertType.WARNING
          }, 2000);
        }
      },
      complete: () => {
        this.loading = false;
      },
      error: () => {
        return
      }
    });
  }

  loadFile(fileID: string, fileName: string) {
    if (confirm('Wollen Sie die Datei ' + fileName + ' herunterladen?')) {
      this.filesService.downloadFile(fileID)
        .subscribe(
          {
            next: data => {
              saveAs(data, fileName);
            },
            complete: () => {
              this.alertDispatcher.show({
                message: ' Die Datei ' + fileName + ' wurde erfolgreich heruntergeladen! ',
                clrAlertType: IAlertType.INFO
              }, 5000);
            },
            error: () => {
              this.alertDispatcher.show({
              message: ' Der Download ist fehlgeschlagen! ',
                clrAlertType: IAlertType.WARNING
              }, 5000);
            }
          });
    }
  }

  deleteFile(fileID: string, fileName: string) {
    if (confirm('Wollen Sie die Datei ' + fileName + ' wirklich löschen?\nDiese Aktion kann nicht rückgängig gemacht werden!')) {
      this.filesService.deleteFile(fileID)
        .subscribe({
          next: () => {
            this.componentsFiles = this.componentsFiles.filter(file => file.id !== fileID);
            this.getFiles([0], this.componentID)
          },
          complete: () => {
            this.alertDispatcher.show({
              message: ' Die Datei ' + fileName + ' wurde erfolgreich gelöscht! ',
              clrAlertType: IAlertType.INFO
            }, 5000);
          },
          error: err => {
            this.alertDispatcher.show({
              message: ' ERROR: Datei ' + fileName + ' konnte nicht gelöscht werden! ' + err,
              clrAlertType: IAlertType.WARNING
            }, 5000);
          }
        });
    }
  }

  showModal(fileid: string, size: string, headline: string) {
    const phmToken = this.authService.getAccessToken();
    const base = `${this.endpoint}/download_file`;
    this.modalUrl = phmToken
      ? `${base}?file_id=${encodeURIComponent(fileid)}&token=${encodeURIComponent(phmToken)}`
      : `${base}?file_id=${encodeURIComponent(fileid)}`;
    this.modalContent = headline;
    this.modalSize = size;
    this.showPDF = true;
  }

  closeModal(state: boolean) {
    this.showFileUpload = state;
    this.getFiles([0], this.componentID);
  }

  showFileUploader(size: string) {
    this.modalSize = size;
    this.showFileUpload = true;
  }

  async downloadZIP(selectedFiles: Files[]) {
    const phmToken = this.authService.getAccessToken();
    const url = phmToken
      ? `${this.endpoint}/download_file?token=${encodeURIComponent(phmToken)}`
      : `${this.endpoint}/download_file`;
    const zip = new JSZip();

    if (this.devMode) console.log("ZIP File will be created with " + selectedFiles.length + " files.");
    if (!confirm(`Wollen Sie die ${selectedFiles.length} Datei(en) in ein ZIP-File packen und herunterladen?`)) {
      return;
    }

    this.isDownloading = true;
    this.downloadProgress = 0;

    const filePromises = selectedFiles.map(async (file, index) => {
      const response = await fetch(`${url}&file_id=${file.id}`);
      const data = await response.blob();
      zip.file(file.fileName, data);
      this.downloadProgress = Math.round(((index + 1) / selectedFiles.length) * 100);
    });

    await Promise.all(filePromises);

    const content = await zip.generateAsync({ type: 'blob' });
    const objectUrl = URL.createObjectURL(content);
    const link = document.createElement('a');
    link.download = `component-${this.componentID}-files.zip`;
    link.href = objectUrl;
    link.click();

    this.isDownloading = false;
    this.downloadProgress = 0;

    this.alertDispatcher.show({
      message: ` Download von ${selectedFiles.length} Dateien erfolgreich! `,
      clrAlertType: IAlertType.SUCCESS
    }, 5000);
  }
}
